import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  MenuItem,
  Select,
  SelectChangeEvent,
  IconButton,
  Popover,
  Snackbar,
  SpeedDial,
  SpeedDialAction,
  type SpeedDialProps,
  Tooltip,
  Typography,
  Chip,
} from '@mui/material'
import { alpha, type Theme } from '@mui/material/styles'
import SendIcon from '@mui/icons-material/Send'
import RefreshIcon from '@mui/icons-material/Refresh'
import FavoriteIcon from '@mui/icons-material/Favorite'
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined'
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined'
import SlideshowOutlinedIcon from '@mui/icons-material/SlideshowOutlined'
import useRouter from '../hooks/useRouter'
import useAccount from '../hooks/useAccount'
import useAiAccess from '../hooks/useAiAccess'
import useWhoToAsk from '../hooks/useWhoToAsk'
import { chatActionReason } from '../utils/chatActionReason'
import { aiAccessRefusalMessage, AI_ACCESS_CODE } from '../utils/aiAccess'
import { createAgentSession, DEFAULT_PERSONA_NAME } from '../utils/agentSession'
import { useDisplayNames, useJobLabel } from '../utils/displayNames'
import useSnackbarTopOffset from '../hooks/useSnackbarTopOffset'
import { snackbarPlacement } from '../contexts/snackbar'
import { useSnackbar } from '../hooks/useSnackbar'
import { useComposerDraft, AgentSidePanel, ChatInputToolbar, ChatNotice, ComposerCard, EdgeReveal, SIDE_PANEL_HISTORY_KEY, SidePanelLauncher, WorkingSessionsBadge } from '../agent'
import TalkButton from '../agent/components/TalkButton'
import type { AgentOutputItem, AgentSidePanelTab } from '../agent'
import VerticalSplitOutlinedIcon from '@mui/icons-material/VerticalSplitOutlined'
import { buildAgentSidePanelTabs } from '../components/agent/agentSidePanelTabs'
import AgentAttachmentPreview from '../components/agent/AgentAttachmentPreview'
import useComposerChips, { type ComposerChip } from '../hooks/useComposerChips'
import PersonaInfoModal from '../components/agent/PersonaInfoModal'
import SessionOptionsDialog from '../components/agent/SessionOptionsDialog'
import { profileForMode, type AgentMode } from '../agent/chiefOfStaff'
import AgentStartBackdrop from '../components/agent/AgentStartBackdrop'
import { customerStartCovers, initialCoverIndex, favouriteCoverSlug, setFavouriteCoverSlug, BrandCover } from '../components/agent/customerBackdrops'
import useBrand from '../hooks/useBrand'
import { AGENT_COLUMN_MAX_WIDTH } from '../components/agent/layout'
// NOT constants/agentModels: that list is hardcoded and had gone stale (Opus
// 4.5 / Sonnet 4.5). The offering comes from the server, which also gates every
// message against it, so the picker cannot offer a model the backend refuses.
import useAgentModels from '../hooks/useAgentModels'
import useFileAttachments from '../hooks/useFileAttachments'
import useVoiceDictation from '../hooks/useVoiceDictation'
import { API_BASE_URL } from '../constants/system'
import { useChatHistoryActions } from '../hooks/useChatHistoryActions'
import { useOutputSearch, outputDestination } from '../hooks/useOutputSearch'
import { AgentSessionListItem } from '../types/agent'
import axios from 'axios'
import { useVoiceRegistration } from '../voice/useVoiceRegistration'
import usePersonas from '../hooks/usePersonas'
import { buildVoiceTools } from '../voice/buildVoiceTools'
import useAlertSetupDraft from '../hooks/useAlertSetupDraft'
import AlertSetupCard from '../components/alerts/AlertSetupCard'
import { extractErrorMessage } from '../utils/apitools'
import usePresentationSeed from '../hooks/usePresentationSeed'
import { useWalkthrough, walkthroughSlugOf } from '../help/walkthroughContext'
import { useGuidedMine } from '../hooks/useGuidedResponse'
import GuidedResumeBox from '../components/guided/GuidedResumeBox'
import { resumableGuides } from '../utils/guided'

const SESSION_PAGE_SIZE = 20

/**
 * How long the start screen takes to become the conversation (#1294).
 *
 * Send does not jump to a new page. It plays three beats, one after the other,
 * and only the last one is a navigation:
 *
 *   1. `fading`   — the ground and the chrome go. The photograph, the heading,
 *                   the pills, the chips and the refresh button all fade out;
 *                   the composer holds still, fully visible.
 *   2. `settling` — the composer travels to the bottom edge on the now-plain
 *                   ground, losing its lift and its rounded card corners as it
 *                   goes, arriving where the chat composer already sits.
 *   3. the rise   — the chat page takes over and its message column rises out
 *                   of the bottom of the screen (see `RISE_MARKER_PREFIX`).
 *
 * Because the composer ends the second beat exactly where the chat page draws
 * its own, the navigation between beats 2 and 3 is not a visible cut.
 *
 * The durations are deliberately unhurried (Adam, 8 Sep: "slower and more
 * deliberate, it's too quick"). Exported so the tests and the chat page read the
 * same numbers rather than restating them.
 */
export const FADE_MS = 600
export const SETTLE_MS = 800

/** The canvas is one of three states, and every transition is driven off it. */
type StartPhase = 'idle' | 'fading' | 'settling'

/**
 * One-shot handover to the chat page: "you were opened by Send, so play the
 * rise". Written next to the initial payload and read (and removed) on the
 * chat page's first render. Absent on a reload, a deep link or a history
 * navigation, which is exactly when the entrance would be wrong.
 */
const RISE_MARKER_PREFIX = 'agent-enter-rise-'

function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches
  } catch {
    return false
  }
}

/** Beat 2's easing/duration: everything that travels does so on this curve. */
const MOTION = `${SETTLE_MS}ms cubic-bezier(0.4, 0, 0.2, 1)`
/** Beat 1's easing/duration: everything that simply goes away uses this one. */
const FADE = `${FADE_MS}ms ease-out`
// Collapse curve for the blocks that fold away. Their max-height is deliberately
// set well above any real content so nothing is ever clipped at rest; this curve
// falls steeply at the start, so the collapse still begins immediately rather
// than idling through the slack. It runs with beat 2, because the column can
// only close up once the chrome on it has finished fading.
const COLLAPSE = `${SETTLE_MS}ms cubic-bezier(0.16, 1, 0.3, 1)`
const REDUCED_MOTION_OFF = { '@media (prefers-reduced-motion: reduce)': { transition: 'none' } }

/**
 * How long the refresh button's icon spins for on click (#1294 follow-up).
 * A remount-on-key CSS animation, not a timed state — see the icon below.
 */
const COVER_SPIN_MS = 200

/** One glass style for the cover dial and its actions: they read as a set. */
const COVER_CONTROL_SX = {
  color: '#fff',
  backgroundColor: 'rgba(255,255,255,0.16)',
  backdropFilter: 'blur(6px)',
  '&:hover': { backgroundColor: 'rgba(255,255,255,0.26)' },
} as const

function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms))
}

function preloadCover(url: string): void {
  try {
    new Image().src = url
  } catch {
    // Best-effort — a failed preload just means the next crossfade may flash.
  }
}

/** Talk's view of the start screen: no chat, so no messages. */
const NO_TALK_MESSAGES: never[] = []

export default function AgentCreatePage() {
  const router = useRouter()
  const account = useAccount()
  const snackbarTopOffset = useSnackbarTopOffset()
  const customer = router.params.customer || account.customer?.name || ''
  const labelJob = useJobLabel(customer)
  // Douglas says this aloud, so it is the customer's name, not its code.
  const customerLabel = useDisplayNames(customer).customer
  // AI access (#1537): starting a session needs level 3. Below it the
  // composer is disabled and says why; the server refuses too.
  const aiAccess = useAiAccess(customer)
  const agentAllowed = aiAccess.can('agent_sessions')
  const snackbar = useSnackbar()
  const whoToAsk = useWhoToAsk(customer)
  // Unfinished guided conversations, offered at the top of History
  // (Matt, 29 Oct 2026): guided chats are kept out of the chat list.
  const resumable = resumableGuides(useGuidedMine(customer).items)
  // Alert mode (alerts round, 12 Oct): `?alert=new` or `?alert=<id>` puts the
  // alert card above the composer. Nothing is saved until Send, which saves
  // the rule, opens the chat on the alert's job and ties the two together.
  const alertParam = (router.params.alert as string | undefined) || ''
  const alertMode = Boolean(alertParam)
  // On-image mode (#1294): a customer with covers switches the start screen
  // to a light treatment so its type reads over the photograph underneath.
  // The covers are the customer's Brand assets folder (#1382) once it has
  // answered; the code catalogue stands in before that and for a customer
  // with no folder. Only which cover is currently shown is state.
  const brand = useBrand(customer)
  const catalogueCovers = useMemo(() => customerStartCovers(customer), [customer])
  const covers = brand.covers.length > 0 ? brand.covers : catalogueCovers
  const coversKey = customer + '|' + covers.map(c => c.slug).join(',')
  const tone: 'light' | 'dark' = covers.length > 0 ? 'light' : 'dark'

  // Which cover is on screen. The hearted cover if this browser has one,
  // otherwise one at random (12 Sep call, #1347 item 16), then advanced in
  // catalogue order by the refresh button — a render-phase ref guard keyed
  // on the cover set, following the same one-shot-per-key pattern this page
  // already uses for personas/session users, so no effect is needed. When
  // the folder answers after the catalogue has shown, the cover on screen
  // stays if the folder has a file of the same name.
  const [coverIndex, setCoverIndex] = useState<number | null>(null)
  const [coverSpinKey, setCoverSpinKey] = useState(0)
  const [favouriteSlug, setFavouriteSlug] = useState<string | null>(null)
  const [coverInfoAnchor, setCoverInfoAnchor] = useState<HTMLElement | null>(null)
  const [coverDialOpen, setCoverDialOpen] = useState(false)
  const coversKeyRef = useRef('')
  const shownSlugRef = useRef<string | null>(null)

  if (coversKey !== coversKeyRef.current) {
    const sameCustomer = coversKeyRef.current.startsWith(customer + '|')
    coversKeyRef.current = coversKey
    const favourite = favouriteCoverSlug(customer)
    setFavouriteSlug(favourite)
    const kept = sameCustomer && shownSlugRef.current ? covers.findIndex(c => c.slug === shownSlugRef.current) : -1
    const initialIndex = covers.length === 0 ? null : kept >= 0 ? kept : initialCoverIndex(covers, favourite)
    setCoverIndex(initialIndex)
    if (initialIndex !== null) {
      preloadCover(covers[(initialIndex + 1) % covers.length].url)
    }
  }

  const currentCover: BrandCover | undefined = coverIndex !== null ? covers[coverIndex] : undefined
  shownSlugRef.current = currentCover?.slug ?? null

  const [isCreating, setIsCreating] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // The `code` a session-create 403 carried alongside `error` (#1537 PR3:
  // 'ai_access_level', 'ai_budget', or any later refusal code), so a refusal
  // draws the notice card rather than the generic error Alert. Null for every
  // other create failure.
  const [errorCode, setErrorCode] = useState<string | null>(null)
  // The notice above the composer (#1537 PR3): the pre-emptive level check
  // wins over a stale create failure, same rule AgentChatPageV2 uses for a
  // running session. A budget refusal (from the create 403) never offers
  // "Request access" — the level is fine, the money isn't, and requesting a
  // higher level would not help; a level refusal does, whether it came from
  // the pre-check or the create call itself.
  const createRefusal = errorCode ? error : null
  const startRefusal = !agentAllowed ? aiAccess.refusalFor('agent_sessions') : createRefusal
  // Pre-emptive: only when the LEVEL is what refuses, not the budget.
  const startRefusalOffersRequest = !agentAllowed ? !aiAccess.levelAllows('agent_sessions') : errorCode === AI_ACCESS_CODE
  const handleRequestAgentAccess = async () => {
    try {
      const res = await aiAccess.handleRequestAccess({ feature: 'agent_sessions' })
      snackbar.success(
        res.notified_admins > 0
          ? `Sent — ${res.notified_admins} ${res.notified_admins === 1 ? 'person' : 'people'} notified.`
          : 'Sent, but no Realm admin was found for this customer. Contact Bayes Price.'
      )
    } catch (err) {
      snackbar.error(aiAccessRefusalMessage(err) ?? 'Could not send the request. Try again.')
    }
  }
  const [sessionList, setSessionList] = useState<AgentSessionListItem[]>([])
  const [hasMoreSessions, setHasMoreSessions] = useState(false)

  // The session's jobs, a multi-select. It opens with exactly one ticked — the
  // job the user was on before clicking the Agent tab — and they may tick
  // more, all, or none. There is deliberately NO "All jobs" default: that is
  // too wide a search area, so the scope is always a conscious choice (Kai,
  // 10 Sep). None ticked is reachable here but a session cannot START in it —
  // Send refuses with an info message instead (see handleCreateSession).
  // Mid-session the set can still change (the Jobs tab, or the agent's
  // set_jobs tool); the gateway re-reads it from the session row per request.
  const [selectedJobs, setSelectedJobs] = useState<string[]>(() => account.job?.name ? [account.job.name] : [])
  // The header's job picker is visible on this page (#1347) and it is the
  // obvious way to change the job a session will start on, so a pick there
  // replaces the ticked set. A render-phase guard, not an effect: React
  // re-renders straight away with the new set, before anything is drawn.
  const headerJobRef = useRef(account.job?.name)
  if (headerJobRef.current !== account.job?.name) {
    headerJobRef.current = account.job?.name
    setSelectedJobs(account.job?.name ? [account.job.name] : [])
  }
  const selectedJob = selectedJobs[0] || ''
  // "Start a new chat from here" (presentations): the pending seed, drawn as
  // a chip; Send copies the presentation into the new chat. The chat starts
  // on the source's job, the same render-phase guard as the header's job.
  // A seed a walkthrough made shows only while that walkthrough runs.
  const presentationSeed = usePresentationSeed(customer, walkthroughSlugOf(useWalkthrough()?.state))
  const seed = alertMode ? null : presentationSeed.seed
  const seedKey = seed ? `${seed.source.kind}:${seed.source.id}:${seed.job}` : null
  const seedJobRef = useRef<string | null>(null)
  if (seedJobRef.current !== seedKey) {
    seedJobRef.current = seedKey
    if (seed?.job && !selectedJobs.some(j => j.toLowerCase() === seed.job.toLowerCase())) setSelectedJobs([seed.job])
  }
  const [persona, setPersona] = useState('')
  const personasLibrary = usePersonas({ customer: customer || '' })
  // The offering, and the default within it, come from the server — the same
  // list that gates every message, so the picker cannot offer a model the
  // backend would clamp away.
  const agentModels = useAgentModels()
  const [selectedModel, setSelectedModel] = useState('')
  // Adopt the server's default once, when it arrives, without clobbering a
  // choice the user has already made.
  const modelDefaultApplied = useRef(false)
  // Send plays its beats before it navigates; a user who has left the page
  // by then must not be pulled into the new chat.
  const unmountedRef = useRef(false)
  useEffect(() => {
    unmountedRef.current = false
    return () => { unmountedRef.current = true }
  }, [])
  if (!modelDefaultApplied.current && agentModels.defaultModel) {
    modelDefaultApplied.current = true
    if (selectedModel === '') setSelectedModel(agentModels.defaultModel)
  }
  // Unsent text survives a trip to another page (#1836).
  const [initialQuestion, setInitialQuestion, clearDraft] = useComposerDraft(customer ? `start:${customer}` : undefined)
  // A seed's starting question pre-fills the composer once per seed, after
  // anything already typed; the person edits it or presses Send. It is
  // composer text, not part of the seed the server receives.
  const seedQuestion = seed?.question ?? ''
  const seedQuestionKey = seedKey && seedQuestion ? `${seedKey}:${seedQuestion}` : null
  useEffect(() => {
    if (!seedQuestionKey) return
    setInitialQuestion(prev => (prev.includes(seedQuestion) ? prev : prev.trim() ? `${prev.trimEnd()}\n\n${seedQuestion}` : seedQuestion))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seedQuestionKey])
  const [personaInfoOpen, setPersonaInfoOpen] = useState(false)
  const [optionsOpen, setOptionsOpen] = useState(false)
  // Send with no job ticked: an info (blue, not red) message, not a session.
  const [noJobsNotice, setNoJobsNotice] = useState(false)
  const [isLaunching, setIsLaunching] = useState(false)
  const [phase, setPhase] = useState<StartPhase>('idle')
  // The session history is off the canvas: nothing but the question is on screen
  // when the page opens (#1273 D1). It is one click away on the launcher in the
  // top-left corner, the same one the chat page has, which opens the panel on
  // the tab it names (`drawerTab`; undefined = the panel's default).
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [drawerTab, setDrawerTab] = useState<string | undefined>(undefined)
  // The same side panel as a session (Adam, 10 Sep): Chats, Jobs, Tables and
  // Variables before the first message. A ticked job is the session's job;
  // a ticked table or variable rides into the first message as a chip.
  const { chips, chipIds, toggleChip, removeChip, clear: clearChips, buildMessage } = useComposerChips()
  const [preview, setPreview] = useState<ComposerChip | null>(null)
  const [userFilter, setUserFilter] = useState('me')
  const [sessionUsers, setSessionUsers] = useState<string[]>([])
  const [searchQuery, setSearchQuery] = useState('')
  const [isSearching, setIsSearching] = useState(false)
  // The search the list is showing (#1634): the server filters the list, so
  // the 5s refresh and Load more carry it too.
  const appliedQueryRef = useRef('')
  // The Page helpers filter ('' = page helper chats left out), sent as ?page=.
  const [pageFilter, setPageFilter] = useState('')
  const pageFilterRef = useRef('')
  const usersLoadedRef = useRef('')
  const sessionListRef = useRef(sessionList)
  sessionListRef.current = sessionList
  const fileAttachments = useFileAttachments()
  const handleTranscription = useCallback((text: string) => {
    setInitialQuestion(prev => prev ? prev + ' ' + text : text)
  }, [])
  const voiceDictation = useVoiceDictation({ onTranscription: handleTranscription })

  const handleToggleRecording = useCallback(() => {
    if (voiceDictation.isRecording) {
      voiceDictation.stopRecording()
    } else {
      voiceDictation.startRecording()
    }
  }, [voiceDictation.isRecording, voiceDictation.stopRecording, voiceDictation.startRecording])

  const listSessions = useCallback(async (cust: string, userEmail?: string) => {
    const query = appliedQueryRef.current
    try {
      const params = new URLSearchParams({ limit: String(SESSION_PAGE_SIZE) })
      if (cust) params.set('customer', cust)
      if (userEmail) params.set('user_email', userEmail)
      if (query) params.set('q', query)
      if (pageFilterRef.current) params.set('page', pageFilterRef.current)
      params.set('exclude_workflow_prefix', 'assistant-')
      const response = await axios.get<{ sessions: AgentSessionListItem[] }>(
        `${API_BASE_URL}/agent/sessions?${params.toString()}`
      )
      // The search changed while this was in flight: a later fetch owns the list.
      if (appliedQueryRef.current !== query) return
      const freshSessions = response.data.sessions || []
      setSessionList(prev => {
        if (query) {
          setHasMoreSessions(freshSessions.length >= SESSION_PAGE_SIZE)
          return freshSessions
        }
        if (prev.length <= SESSION_PAGE_SIZE) {
          setHasMoreSessions(freshSessions.length >= SESSION_PAGE_SIZE)
          return freshSessions
        }
        const freshMap = new Map(freshSessions.map(s => [s.id, s]))
        const updated = prev.map(s => freshMap.get(s.id) ?? s)
        const existingIds = new Set(prev.map(s => s.id))
        const newSessions = freshSessions.filter(s => !existingIds.has(s.id))
        return newSessions.length > 0 ? [...newSessions, ...updated] : updated
      })
    } catch (err) {
      console.error('Failed to list sessions:', err)
    }
  }, [])

  const loadMoreSessions = useCallback(async (cust: string, userEmail?: string) => {
    try {
      const params = new URLSearchParams({
        has_messages: 'true',
        limit: String(SESSION_PAGE_SIZE),
        offset: String(sessionListRef.current.length),
      })
      if (cust) params.set('customer', cust)
      if (userEmail) params.set('user_email', userEmail)
      if (appliedQueryRef.current) params.set('q', appliedQueryRef.current)
      if (pageFilterRef.current) params.set('page', pageFilterRef.current)
      params.set('exclude_workflow_prefix', 'assistant-')
      const response = await axios.get<{ sessions: AgentSessionListItem[] }>(
        `${API_BASE_URL}/agent/sessions?${params.toString()}`
      )
      const newSessions = response.data.sessions || []
      setSessionList(prev => [...prev, ...newSessions])
      setHasMoreSessions(newSessions.length >= SESSION_PAGE_SIZE)
    } catch (err) {
      console.error('Failed to load more sessions:', err)
    }
  }, [])

  const loadSessionUsers = useCallback(async (cust: string): Promise<string[]> => {
    try {
      const response = await axios.get<{ users: string[] }>(
        `${API_BASE_URL}/agent/sessions/users?customer=${encodeURIComponent(cust)}`
      )
      return response.data.users || []
    } catch (err) {
      console.error('Failed to load session users:', err)
      return []
    }
  }, [])

  useEffect(() => {
    if (!customer) return
    const emailParam = userFilter === 'me' ? undefined : userFilter
    listSessions(customer, emailParam)
    const timer = setInterval(() => listSessions(customer, emailParam), 5000)
    return () => clearInterval(timer)
  }, [customer, userFilter]) // listSessions is stable ([] deps) — not included to avoid lint noise

  if (customer && usersLoadedRef.current !== customer) {
    usersLoadedRef.current = customer
    loadSessionUsers(customer).then(setSessionUsers)
  }

  // The customer's personas library, Company-level then the user's own
  // (#1523). It used to be read per job, from the first ticked job's config.
  const allPersonas = personasLibrary.pickerPersonas
  if (allPersonas.length > 0 && !persona) {
    const defaultPersona = allPersonas.find(p => p.name === DEFAULT_PERSONA_NAME) || allPersonas[0]
    setPersona(defaultPersona.name)
  }

  const personaNames = personasLibrary.personaNames
  const customPersonasForModals = allPersonas

  const availableJobs = (account.customer?.sessionJobs || [])
    .filter((j): j is { name: string; displayName?: string } => !!j.name)

  const alertSetup = useAlertSetupDraft({
    customer,
    alertParam,
    defaultJob: availableJobs.some((j) => j.name === account.job?.name) ? account.job?.name || '' : availableJobs[0]?.name || '',
    viewerEmail: account.user?.session_info?.email || '',
    whoToAsk,
    jobLabel: labelJob,
  })
  // A new alert also needs alert_create (the server refuses the POST without it).
  const alertCreateRefusal = alertParam === 'new' && !aiAccess.can('alert_create') ? aiAccess.refusalFor('alert_create') : undefined
  const alertBlocked = alertMode && Boolean(alertCreateRefusal || alertSetup.lockedReason || alertSetup.loading || alertSetup.loadError)

  // `jobsOverride`: an alert's chat works on the alert's job only.
  // Chief of Staff mode: a Manager's checkbox in Session options
  // (design/2026-10-26-chief-of-staff-agents-panel.md T1), sent as the
  // session's profile. An alert's chat is always Normal.
  const [mode, setMode] = useState<AgentMode>('normal')
  const isManager = account.user?.role_permissions?.isManager ?? false
  const createSessionV2 = useCallback(async (jobsOverride?: string[]): Promise<string | null> => {
    const jobs = jobsOverride ?? selectedJobs
    const profile = !jobsOverride && isManager ? profileForMode(mode) : undefined
    return createAgentSession(customer, {
      job: jobsOverride ? jobs[0] || '' : selectedJob,
      jobs,
      persona: persona || DEFAULT_PERSONA_NAME,
      model: selectedModel,
      ...(profile ? { profile } : {}),
    })
  }, [customer, selectedJob, selectedJobs, persona, selectedModel, mode, isManager])

  // Alert mode's create: save the rule first (its contents are durable from
  // here), then the session on the alert's job, then tie them together so
  // the server adds the setup instructions to every turn of that chat.
  const startAlertSession = async (): Promise<string | null> => {
    let rule
    try {
      rule = await alertSetup.handleSaveRule()
    } catch (err) {
      throw new Error(extractErrorMessage(err) || 'Could not save the alert')
    }
    // Back from the chat now reopens the saved alert, filled in, and a second
    // Send after a refusal changes it rather than making another.
    if (alertParam !== rule.id) router.setParams({ alert: rule.id }, false, { history: 'replace' })
    const sessionId = await createSessionV2([alertSetup.draft.job])
    if (!sessionId) return null
    try {
      await alertSetup.handleAttachSession(rule.id, sessionId)
    } catch (err) {
      // Not tied: this chat would sit empty in History and the next Send would
      // make another. Remove it (best effort); the saved rule and the card stay.
      await axios.delete(`${API_BASE_URL}/agent/session/${sessionId}`).catch(() => undefined)
      throw err
    }
    return sessionId
  }

  // A seeded chat's create: the session, then the copy of the presentation.
  // A failed copy deletes the session (usePresentationSeed) and the chip stays.
  const startSeededSession = async (): Promise<string | null> => {
    const sessionId = await createSessionV2()
    if (!sessionId) return null
    await presentationSeed.handleSeedSession(sessionId)
    return sessionId
  }

  // `spoken`: Talk's words (T20), sent in place of the composer's text.
  const handleCreateSession = useCallback(async (spoken?: string) => {
    if (isCreating || isLaunching || !agentAllowed) return
    const question = spoken ?? initialQuestion
    // An alert's card is the message; the composer adds to it, optionally.
    if (!alertMode && !question.trim()) return
    if (alertMode) {
      if (alertBlocked || !alertSetup.handleValidate()) return
    } else if (selectedJobs.length === 0) {
      // A session starts with jobs the user chose, never with none: say so here,
      // in the UI, rather than creating a session whose agent can only refuse.
      setNoJobsNotice(true)
      return
    }
    // The server copies a presentation only into a chat on its job.
    if (seed && !selectedJobs.some(j => j.toLowerCase() === seed.job.toLowerCase())) {
      setError(`This chat starts from “${seed.label}”, which is on ${labelJob(seed.job)}. Tick that job in Session options, or remove the presentation.`)
      setErrorCode(null)
      return
    }
    setIsLaunching(true)
    setError(null)
    setErrorCode(null)
    const reduced = prefersReducedMotion()
    // Beat 1 starts now, alongside the create request — the wait and the
    // animation overlap instead of queueing. The request's outcome is captured
    // rather than thrown, so awaiting a beat can never leave it unhandled.
    setPhase('fading')
    const startedAt = Date.now()
    const created = (alertMode ? startAlertSession() : seed ? startSeededSession() : createSessionV2()).then(
      (id: string | null) => ({ id }),
      (err: unknown) => ({ err }),
    )
    try {
      // Beat 1 → beat 2. Under reduced motion there are no beats at all.
      if (!reduced) await sleep(FADE_MS)
      setPhase('settling')

      const outcome = await created
      if ('err' in outcome) throw outcome.err
      const sessionId = outcome.id
      if (!sessionId) return

      let attachmentIds: string[] | undefined
      if (fileAttachments.attachments.length > 0) {
        attachmentIds = await fileAttachments.uploadAll(sessionId)
        fileAttachments.clear()
      }

      const words = question.trim()
      const payload = JSON.stringify({
        message: buildMessage(alertMode ? alertSetup.firstMessage(words) : words),
        model: selectedModel,
        ...(attachmentIds?.length ? { attachmentIds } : {}),
      })
      sessionStorage.setItem(`agent-initial-payload-${sessionId}`, payload)
      clearDraft()
      clearChips()
      if (seed) presentationSeed.handleUsed()
      // Beat 3 is the chat page's to play, and only because this Send asked for
      // it. Under reduced motion the marker is never written, so the chat page
      // renders with no entrance at all.
      if (!reduced) sessionStorage.setItem(`${RISE_MARKER_PREFIX}${sessionId}`, '1')
      // Hold the cut until beats 1 and 2 have actually played.
      const remaining = reduced ? 0 : FADE_MS + SETTLE_MS - (Date.now() - startedAt)
      if (remaining > 0) await sleep(remaining)
      if (unmountedRef.current) return
      router.navigate('agent-session', { customer, sessionId })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create session')
      setErrorCode(err instanceof Error ? (err as Error & { code?: string }).code ?? null : null)
      // Beat 1 in reverse: the ground and the chrome come back.
      setPhase('idle')
    } finally {
      setIsLaunching(false)
    }
  }, [customer, selectedModel, selectedJobs, initialQuestion, isCreating, isLaunching, agentAllowed, createSessionV2, router, fileAttachments, alertMode, alertBlocked, alertSetup, startAlertSession, startSeededSession, seed, presentationSeed, labelJob])

  const handlePersonaChange = useCallback((e: SelectChangeEvent<string>) => {
    setPersona(e.target.value)
  }, [])

  const handleJobsChange = useCallback((e: SelectChangeEvent<string[]>) => {
    const v = e.target.value
    setSelectedJobs(typeof v === 'string' ? v.split(',') : v)
  }, [])

  // Voice manifest (#1323): what Douglas can do on the start screen. Without
  // it he could describe the composer but not type into it or send, which is
  // what the first guided tours ran into.
  // Tools are snapshotted when the page registers (per pageKey), so a closure
  // over state or over a useCallback that depends on state goes stale the
  // moment Douglas types: send_question saw the empty composer from before
  // set_question ran and refused (10 Sep, on dev). Live values ride in a ref.
  const voiceCanSend = !!initialQuestion.trim() && !isCreating && !isLaunching && agentAllowed
  const startVoiceRef = useRef({ canSend: voiceCanSend, create: handleCreateSession })
  startVoiceRef.current = { canSend: voiceCanSend, create: handleCreateSession }
  useVoiceRegistration({
    pageKey: `agent-new:${customer}`,
    pageContext: () =>
      `The agent start screen for ${customerLabel}: one composer on the company cover, placeholder "What would you like to do today?". ` +
      `The person types (or you set) a question and Send opens a session with it as the first message. ` +
      `Session options (the sliders icon) is where the job (Platinum's word for a project or study) is chosen: a multi-select, and at least one must be ticked to start. It also holds Persona${isManager && !alertMode ? ', and the Chief of Staff mode checkbox (Managers only: the agent can hand parts of the work to sub-agents and workflows)' : ''}. The toolbar also has the attachment clip and the microphone. The left edge opens the session history drawer (previous sessions). ` +
      `${selectedJobs.length === 0 ? 'Current job: none ticked — Send will ask for one' : selectedJobs.length === 1 ? `Current job: ${selectedJobs[0]}` : `Current jobs: ${selectedJobs.join(', ')}`}. Composer text right now: ${initialQuestion.trim() ? JSON.stringify(initialQuestion) : '(empty)'}.`,
    uiTools: buildVoiceTools({
      fields: [{ name: 'set_question', label: 'the question in the composer', set: (v) => setInitialQuestion(v) }],
      choices: [{
        name: 'choose_model',
        label: 'the AI model the session uses (the picker in the composer toolbar)',
        // Douglas sees the tier names (Best, Good), never a model id.
        options: agentModels.models.map((m) => ({ value: m.label, describe: m.description || m.label })),
        select: (v) => {
          const id = agentModels.models.find((m) => m.label === v)?.id
          if (!id) return `no model called ${v}`
          setSelectedModel(id)
          return `model set to ${v}`
        },
      }, {
        // Sets the selection to exactly this one job. Ticking several is a
        // job for the dialog; a single spoken choice replaces the set.
        name: 'choose_job',
        label: 'the one job (Platinum\'s word for a project or study) the session works in, replacing any others ticked; Session options is where a person picks several by hand',
        options: availableJobs.map((j) => ({ value: j.name, describe: j.displayName || j.name })),
        select: (v) => { setSelectedJobs([v]); return `job set to ${v}` },
      }],
      actions: [
        { name: 'send_question', label: 'send the question and start the session', run: () => startVoiceRef.current.create(), enabled: () => startVoiceRef.current.canSend, note: 'Only when the composer has text. Opens the session with the existing animation.' },
        { name: 'open_session_options', label: 'open Session options (job, persona, the Chief of Staff mode checkbox for Managers, start from)', run: () => setOptionsOpen(true) },
        { name: 'close_session_options', label: 'close the Session options dialog', run: () => setOptionsOpen(false) },
        { name: 'open_session_history', label: 'open the left panel: previous sessions, with Jobs, Tables and Variables tabs', run: () => { setDrawerTab(SIDE_PANEL_HISTORY_KEY); setDrawerOpen(true) } },
        { name: 'open_tables_panel', label: 'open the left panel (Jobs, Tables, Variables, previous sessions)', run: () => { setDrawerTab('toc'); setDrawerOpen(true) } },
        { name: 'close_session_history', label: 'close the left panel', run: () => setDrawerOpen(false) },
      ],
    }),
  })

  // Talk (T20): there is no chat yet, so what the person says is sent the way
  // Send sends it, creating the chat; the conversation carries on there. With
  // no job ticked Send only shows its notice, so Talk is told why.
  const talkCreateRef = useRef({ create: handleCreateSession, needsJob: false })
  talkCreateRef.current = { create: handleCreateSession, needsJob: !alertMode && selectedJobs.length === 0 }
  const talkSession = useMemo(() => ({
    session: null,
    messages: NO_TALK_MESSAGES,
    isStreaming: false,
    // A refused create shows its error here; Talk tells the person.
    error,
    sendMessage: (text: string) => {
      const { create, needsJob } = talkCreateRef.current
      if (needsJob) {
        setNoJobsNotice(true)
        throw new Error('No job is ticked yet. Tick one in Session options, then ask again.')
      }
      void create(text)
    },
  }), [error])

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleCreateSession()
    }
  }, [handleCreateSession])

  const sessionEmailParam = userFilter === 'me' ? undefined : userFilter

  // Past-work search (#1649): the outputs past chats made, searched beside
  // the chats with the same query and user filter.
  const { outputs, outputsNotice, handleSearchOutputs, handleClearOutputs } = useOutputSearch(customer)

  const handleUserFilterChange = (value: string) => {
    setUserFilter(value)
    setSearchQuery('')
    appliedQueryRef.current = ''
    handleClearOutputs()
  }

  const handlePageFilterChange = (value: string) => {
    pageFilterRef.current = value
    setPageFilter(value)
    void listSessions(customer, sessionEmailParam)
  }

  const handleSearchChange = (value: string) => {
    setSearchQuery(value)
    if (!value.trim()) handleClearOutputs()
    if (!value.trim() && appliedQueryRef.current) {
      appliedQueryRef.current = ''
      listSessions(customer, sessionEmailParam)
    }
  }

  const handleSearchSubmit = async (query: string) => {
    const trimmed = query.trim()
    if (trimmed.length < 2 || !customer) return
    appliedQueryRef.current = trimmed
    setIsSearching(true)
    void handleSearchOutputs(trimmed, userFilter)
    await listSessions(customer, sessionEmailParam)
    setIsSearching(false)
  }

  // An artifact opens its chat with the preview (?artifact=); a table opens Tables.
  const handleSelectOutput = (output: AgentOutputItem) => {
    const dest = outputDestination(output, customer)
    router.navigate(dest.name, dest.params)
  }

  const handleOpenDrawer = useCallback((tab: string) => {
    setDrawerTab(tab)
    setDrawerOpen(true)
  }, [])
  // The left-edge pill reopens the panel on the tab it last showed.
  const handleRevealDrawer = useCallback(() => setDrawerOpen(true), [])
  const handleCloseDrawer = useCallback(() => setDrawerOpen(false), [])

  const handleSelectSession = useCallback((sessionId: string) => {
    router.navigate('agent-session', { customer, sessionId })
  }, [router, customer])

  // Rename, star and delete from the History tab (#1634). No chat is open on
  // the start screen, so a delete only refreshes the list.
  const { handleRenameSession, handleToggleStar, handleDeleteSession } = useChatHistoryActions({
    refetch: () => listSessions(customer, sessionEmailParam),
    onError: msg => snackbar.error(msg),
  })

  // The living circle astride the composer (#1430) is always drawn. It is
  // alive for the sessions with a turn in flight and rests otherwise; its
  // hover lists those, then the sessions that are Ready (a live container,
  // nothing running — the same reading History gives) as shortcuts. Same
  // list the History tab shows, polled every few seconds above.
  const circleSessions = sessionList
    .filter(s => s.active_query_id || s.container_state === 'running')
    .map(s => s.active_query_id
      ? { id: s.id, title: s.title, state: 'working' as const, startedAt: s.active_turn_started_at }
      : { id: s.id, title: s.title, state: 'ready' as const })

  const handleOpenOptions = useCallback(() => setOptionsOpen(true), [])
  const handleCloseOptions = useCallback(() => setOptionsOpen(false), [])

  // Refresh (#1294 follow-up): the next cover in catalogue order, wrapping —
  // twelve clicks show all twelve.
  const handleChangeBackground = useCallback(() => {
    if (covers.length === 0) return
    const nextIndex = ((coverIndex ?? 0) + 1) % covers.length
    setCoverIndex(nextIndex)
    setCoverSpinKey(k => k + 1)
    preloadCover(covers[(nextIndex + 1) % covers.length].url)
  }, [covers, coverIndex])

  // Heart (#1347 item 16): this cover is what I land on, until I unheart it.
  const isFavourite = currentCover !== undefined && favouriteSlug === currentCover.slug
  const handleToggleFavourite = useCallback(() => {
    if (!currentCover) return
    const next = favouriteSlug === currentCover.slug ? null : currentCover.slug
    setFavouriteCoverSlug(customer, next)
    setFavouriteSlug(next)
  }, [customer, currentCover, favouriteSlug])

  // Two flags, read straight off the phase. `leaving` is true from the moment
  // Send is pressed — it is what makes the ground and the chrome go. `settling`
  // is beat 2 only — it is what makes the column move.
  const leaving = phase !== 'idle'
  const settling = phase === 'settling'
  // Not gated on a job: with none ticked, Send stays clickable and answers with
  // the info message, which says what to do rather than silently greying out.
  // An alert's Send needs no typed words (the card is the message), and stays
  // clickable with gaps in the card: the click is what shows them.
  const canSend = (alertMode ? !alertBlocked : !!initialQuestion.trim()) && !isCreating && !isLaunching && agentAllowed

  // The one tab list (agentSidePanelTabs.tsx), on the start screen's own
  // state: there is no session yet, so a Jobs tick lands in selectedJobs
  // (AgentJobsTab's pre-session mode) and the trees scope to the first one.
  const startSidePanelTabs: AgentSidePanelTab[] = buildAgentSidePanelTabs({
    customer,
    chipIds,
    onToggle: toggleChip,
    onPreview: setPreview,
    useValues: () => ({ sessionId: '', sessionJobs: selectedJobs, fallbackJobs: selectedJobs, onJobsChanged: setSelectedJobs, job: selectedJob, treeJobs: selectedJobs }),
  })

  return (
    // `position: relative` is load-bearing: EdgeReveal's hover zone is absolutely
    // positioned and measures its nearest positioned ancestor.
    <Box sx={{ display: 'flex', height: 'calc(100vh - 64px)', position: 'relative' }}>
      {/* The left-edge pill stays beside the top-left launcher (Adam, 13 Oct). */}
      <EdgeReveal
        side="left"
        label="Open tables panel"
        icon={<VerticalSplitOutlinedIcon />}
        hidden={drawerOpen}
        onOpen={handleRevealDrawer}
      />
      <AgentSidePanel
        open={drawerOpen}
        onClose={handleCloseDrawer}
        tabs={startSidePanelTabs}
        activeKey={drawerTab}
        onActiveKeyChange={setDrawerTab}
        sessions={sessionList}
        activeSessionId={undefined}
        onSelectSession={handleSelectSession}
        onLoadMore={() => loadMoreSessions(customer, userFilter === 'me' ? undefined : userFilter)}
        hasMore={hasMoreSessions}
        users={sessionUsers}
        selectedUserEmail={userFilter}
        onUserFilterChange={handleUserFilterChange}
        searchQuery={searchQuery}
        onSearchChange={handleSearchChange}
        onSearchSubmit={handleSearchSubmit}
        isSearching={isSearching}
        currentUserEmail={account.user?.session_info?.email || undefined}
        onRenameSession={handleRenameSession}
        onDeleteSession={handleDeleteSession}
        onToggleStar={handleToggleStar}
        actionReason={chatActionReason(whoToAsk, account.user?.session_info?.email || undefined, account.user?.role_permissions?.isManager ?? false)}
        labelJob={labelJob}
        outputs={outputs}
        outputsNotice={outputsNotice}
        onSelectOutput={handleSelectOutput}
        pageFilter={pageFilter}
        onPageFilterChange={handlePageFilterChange}
        pinned={<GuidedResumeBox items={resumable} onOpen={(responseId) => router.navigate('guided-response', { customer, responseId })} />}
      />

      {/* The canvas. Everything on it is one column, and the column's three
          states are `idle`, `fading` and `settling` — read them off data-phase. */}
      <Box
        data-testid="agent-start-screen"
        data-phase={phase}
        data-tone={tone}
        component="form"
        // The alert card's required fields are checked by the card itself
        // (draftProblems), which says what is missing in its own words.
        noValidate
        onSubmit={(e: React.FormEvent) => { e.preventDefault(); handleCreateSession() }}
        onDragOver={(e: React.DragEvent) => { e.preventDefault(); e.stopPropagation() }}
        onDrop={(e: React.DragEvent) => {
          e.preventDefault()
          e.stopPropagation()
          if (e.dataTransfer.files.length > 0) {
            fileAttachments.addFiles(e.dataTransfer.files)
          }
        }}
        sx={{ flex: 1, minWidth: 0, position: 'relative', overflow: 'hidden' }}
      >
        {/* Swap `imageUrl` for a customer asset and the whole ground changes;
            nothing above it moves. */}
        <AgentStartBackdrop imageUrl={currentCover?.url} slug={currentCover?.slug} dimmed={leaving} />

        {/* The way into the side panel (#1795 follow-up): the cover dial's
            twin in the opposite corner. Hover or focus unfurls the panel's
            tabs downward; a pick opens the panel on that tab. Glass over a
            cover, a paper button over the swirl. It leaves with the chrome. */}
        <SidePanelLauncher
          tabs={startSidePanelTabs}
          hidden={drawerOpen}
          onOpen={handleOpenDrawer}
          fabSx={currentCover ? { ...COVER_CONTROL_SX, boxShadow: 'none' } : undefined}
          sx={{
            top: 16,
            left: 16,
            opacity: leaving ? 0 : 1,
            transition: `opacity ${FADE}`,
            ...REDUCED_MOTION_OFF,
            // The dial's root takes no pointer already; its button and tabs do.
            ...(leaving ? { '& .MuiSpeedDial-fab, & .MuiSpeedDial-actions': { pointerEvents: 'none' } } : {}),
          }}
        />

        {/* Cover controls (12 Sep call, #1347 item 16): one image button in
            the corner; hover or focus fans out refresh, heart and info above
            it (Adam, 12 Sep: an image icon, options on hover). They go with
            the rest of the chrome on Send, and stop taking clicks the moment
            they start to leave. The dial stays open while the info card is
            up, so the card is not left pointing at nothing. */}
        {currentCover && (
          <SpeedDial
            ariaLabel="Cover options"
            data-testid="agent-start-cover-controls"
            direction="up"
            icon={<ImageOutlinedIcon fontSize="small" />}
            open={coverDialOpen || coverInfoAnchor !== null}
            onOpen={() => setCoverDialOpen(true)}
            onClose={() => setCoverDialOpen(false)}
            FabProps={{ size: 'small', sx: COVER_CONTROL_SX, 'data-testid': 'agent-start-cover-dial' } as SpeedDialProps['FabProps']}
            sx={{
              position: 'absolute',
              right: 16,
              bottom: 16,
              zIndex: 1,
              opacity: leaving ? 0 : 1,
              pointerEvents: leaving ? 'none' : 'auto',
              transition: `opacity ${FADE}`,
              ...REDUCED_MOTION_OFF,
              // The actions share the glass of the main button.
              '& .MuiSpeedDialAction-fab': { ...COVER_CONTROL_SX, boxShadow: 'none' },
            }}
          >
            <SpeedDialAction
              icon={<InfoOutlinedIcon fontSize="small" />}
              tooltipTitle="About this image"
              onClick={e => setCoverInfoAnchor(e.currentTarget)}
              FabProps={{ 'aria-label': 'About this image', 'data-testid': 'agent-start-cover-info' } as SpeedDialProps['FabProps']}
            />
            <SpeedDialAction
              icon={isFavourite ? <FavoriteIcon fontSize="small" /> : <FavoriteBorderIcon fontSize="small" />}
              tooltipTitle={isFavourite ? 'This is my start image' : 'Set as my start image'}
              onClick={handleToggleFavourite}
              FabProps={{
                'aria-label': isFavourite ? 'Unset as my start image' : 'Set as my start image',
                'aria-pressed': isFavourite,
                'data-testid': 'agent-start-cover-favourite',
              } as SpeedDialProps['FabProps']}
            />
            {/* Only a real choice when there's something to cycle to. */}
            {covers.length >= 2 && (
              <SpeedDialAction
                icon={(
                  <RefreshIcon
                    key={coverSpinKey}
                    fontSize="small"
                    sx={{
                      animation: coverSpinKey > 0 ? `agentStartCoverSpin ${COVER_SPIN_MS}ms ease-out` : 'none',
                      '@keyframes agentStartCoverSpin': {
                        from: { transform: 'rotate(0deg)' },
                        to: { transform: 'rotate(180deg)' },
                      },
                      '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
                    }}
                  />
                )}
                tooltipTitle="Change background"
                onClick={handleChangeBackground}
                FabProps={{ 'aria-label': 'Change background', 'data-testid': 'agent-start-cover-refresh' } as SpeedDialProps['FabProps']}
              />
            )}
          </SpeedDial>
        )}
        {currentCover && (
          <Popover
            open={coverInfoAnchor !== null}
            anchorEl={coverInfoAnchor}
            onClose={() => setCoverInfoAnchor(null)}
            anchorOrigin={{ vertical: 'center', horizontal: 'left' }}
            transformOrigin={{ vertical: 'center', horizontal: 'right' }}
            slotProps={{ paper: { sx: { ml: -1, p: 2, maxWidth: 320, borderRadius: '10px' } } }}
          >
            <Box data-testid="agent-start-cover-info-card">
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{currentCover.title}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{currentCover.description}</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
                Information about this image will appear here.
              </Typography>
            </Box>
          </Popover>
        )}

        <Box
          data-testid="agent-start-column"
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            // The composer is docked on the bottom edge of the screen: no gap
            // beneath it, its sides meeting the edge square. It stays docked
            // through beat 2 — the chat sheet's composer is docked too — and
            // only the page gutter goes, so the card takes the sheet's own
            // width. That is what makes the cut between beats 2 and 3
            // invisible: the sheet rises to meet a composer already in its
            // place, the same shape it will keep.
            px: settling ? 0 : { xs: 3, md: 4 },
            pb: 0,
            transition: `padding ${MOTION}`,
            ...REDUCED_MOTION_OFF,
            overflowY: 'auto',
          }}
        >
          {/* Two flex spacers do the whole move. At rest the top one takes all
              the slack, which docks the composer on the bottom edge of the
              cover and leaves the glowing logo across the middle of the frame
              completely clear (Matt's mockup, #1294). On beat 2 the middle one
              takes over so the card keeps its place while the column's padding
              lifts it onto the chat sheet's gutter. */}
          <Box
            data-testid="agent-start-spacer-top"
            // In alert mode the card takes the slack instead, so the whole
            // form shows above the composer rather than squeezed onto it.
            sx={{ flexGrow: settling || alertMode ? 0 : 1, minHeight: alertMode ? { xs: 16, md: 32 } : 0, transition: `flex-grow ${MOTION}`, ...REDUCED_MOTION_OFF }}
          />

          {/* The alert card (alert mode): above the composer, which keeps its
              microphone for anything the user wants to add. It goes with the
              chrome on Send and comes back, as it was, if the Send fails. */}
          {alertMode && (
            <Box
              data-testid="agent-start-alert-card"
              sx={{
                width: '100%',
                maxWidth: AGENT_COLUMN_MAX_WIDTH,
                // Fills the space between the top of the page and the composer,
                // with a clear gap above the composer; the card scrolls inside
                // only when the screen is too short for the whole form.
                flexGrow: settling ? 0 : 1,
                flexShrink: 1,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-end',
                pb: { xs: 2, md: 3 },
                opacity: leaving ? 0 : 1,
                pointerEvents: leaving ? 'none' : 'auto',
                transition: `opacity ${FADE}`,
                ...REDUCED_MOTION_OFF,
              }}
            >
              <AlertSetupCard
                draft={alertSetup.draft}
                isNew={alertSetup.isNew}
                problems={alertSetup.problems}
                jobs={availableJobs}
                toc={alertSetup.toc}
                tocLoading={alertSetup.tocLoading}
                recipientOptions={alertSetup.recipientOptions}
                recipientsLoading={alertSetup.recipientsLoading}
                recipientsError={alertSetup.recipientsError}
                recipientQuery={alertSetup.recipientQuery}
                loading={alertSetup.loading}
                loadError={alertSetup.loadError}
                lockedReason={alertSetup.lockedReason}
                disabled={isCreating || isLaunching || !agentAllowed}
                onChange={alertSetup.handleChange}
                onKindChange={alertSetup.handleKindChange}
                onJobChange={alertSetup.handleJobChange}
                onAddTable={alertSetup.handleAddTable}
                onRemoveTable={alertSetup.handleRemoveTable}
                onRecipientQueryChange={alertSetup.handleRecipientQueryChange}
              />
            </Box>
          )}

          <Box
            data-testid="agent-start-spacer-mid"
            sx={{ flexGrow: settling ? 1 : 0, transition: `flex-grow ${MOTION}`, ...REDUCED_MOTION_OFF }}
          />

          <Box
            data-testid="agent-start-composer-rise"
            sx={{
              width: '100%',
              maxWidth: AGENT_COLUMN_MAX_WIDTH,
              flexShrink: 0,
              // On load the composer rises into view from below the bottom edge
              // — the same direction the chat sheet later rises from on Send.
              '@keyframes agentStartComposerRise': {
                from: { transform: 'translateY(100%)', opacity: 0 },
                to: { transform: 'translateY(0)', opacity: 1 },
              },
              animation: `agentStartComposerRise ${SETTLE_MS}ms cubic-bezier(0.16, 1, 0.3, 1) both`,
              '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
            }}
          >
            {startRefusal || alertCreateRefusal ? (
              <ChatNotice
                severity="error"
                text={startRefusal || alertCreateRefusal || ''}
                action={startRefusal && startRefusalOffersRequest ? (
                  <Button color="info" size="small" onClick={handleRequestAgentAccess}>Request access</Button>
                ) : undefined}
              />
            ) : error && (
              <Alert severity="error" sx={{ mb: 2, fontSize: 13 }}>
                {error}
              </Alert>
            )}

            {/* The composer: the same ComposerCard the session draws, with
                Session options as its leading control, floating on the cover
                (so it keeps a soft shadow). Docked on the bottom edge, and it
                stays docked through the send. */}
            <ComposerCard
              testId="agent-start-composer"
              elevated
              badge={<WorkingSessionsBadge sessions={circleSessions} onOpen={handleSelectSession} />}
              leading={(<>
                <Tooltip title="Session options">
                  <IconButton
                    size="small"
                    aria-label="Session options"
                    data-testid="agent-start-options-button"
                    onClick={handleOpenOptions}
                    disabled={isCreating || isLaunching}
                    sx={{ width: 34, height: 34, flexShrink: 0, color: 'info.main' }}
                  >
                    <TuneOutlinedIcon sx={{ fontSize: 22 }} />
                  </IconButton>
                </Tooltip>
                {/* The presentation this chat starts from (presentations). */}
                {seed && (
                  <Tooltip title={`This chat gets a copy of “${seed.label}”${seed.figureText ? `, about ${seed.figureText}` : ''}${seed.at ? ', open at this point' : ''}. Remove it to start an ordinary chat.`}>
                    <Chip
                      size="small"
                      color="info"
                      variant="outlined"
                      icon={<SlideshowOutlinedIcon />}
                      label={seed.label}
                      onDelete={presentationSeed.handleRemove}
                      disabled={isCreating || isLaunching}
                      data-testid="agent-start-seed-chip"
                      sx={{ maxWidth: 280, alignSelf: 'center' }}
                    />
                  </Tooltip>
                )}
                {/* Talk (T20): the person speaks instead of typing. */}
                {customer && (
                  <TalkButton
                    session={talkSession}
                    customer={customer}
                    disabledReason={!agentAllowed ? aiAccess.refusalFor('agent_sessions') : undefined}
                  />
                )}
              </>)}
              models={agentModels.models}
              selectedModel={selectedModel}
              onModelChange={setSelectedModel}
              modelDisabled={isCreating || isLaunching}
              toolbar={(
                <ChatInputToolbar
                  onFilesSelected={files => fileAttachments.addFiles(files)}
                  attachments={fileAttachments.attachments}
                  onRemoveAttachment={fileAttachments.removeFile}
                  isRecording={voiceDictation.isRecording}
                  isTranscribing={voiceDictation.isTranscribing}
                  onToggleRecording={handleToggleRecording}
                  onStopRecording={voiceDictation.stopRecording}
                  onCancelRecording={voiceDictation.cancelRecording}
                  stream={voiceDictation.stream}
                  error={voiceDictation.error}
                  disabled={isCreating || isLaunching}
                  // Dictation meets the agent-session rule (audit C23).
                  micDisabledReason={!agentAllowed ? aiAccess.refusalFor('agent_sessions') : undefined}
                />
              )}
              chips={chips.map(c => ({ id: c.id, label: c.label, tooltip: c.tooltip }))}
              onRemoveChip={removeChip}
              onChipClick={(id) => setPreview(chips.find(c => c.id === id) ?? null)}
              value={initialQuestion}
              onChange={setInitialQuestion}
              onKeyDown={handleKeyDown}
              onPaste={(e: React.ClipboardEvent) => fileAttachments.handlePaste(e.nativeEvent)}
              placeholder={!agentAllowed
                ? aiAccess.refusalFor('agent_sessions')
                : alertMode ? 'Anything else the agent should know? (optional)' : 'What would you like to do today?'}
              // The same depth as the session composer. Item 17 (12 Sep call)
              // made this three rows; Adam reversed it the same evening once
              // both were on dev side by side: "make /new the same as in a
              // session". The depth was the only difference left.
              disabled={isCreating || isLaunching || !agentAllowed}
              action={isCreating || isLaunching ? 'busy' : 'send'}
              sendDisabled={!canSend}
            />
          </Box>

        </Box>

        <AgentAttachmentPreview chip={preview} customer={customer} onClose={() => setPreview(null)} />
      <SessionOptionsDialog
          open={optionsOpen}
          onClose={handleCloseOptions}
          disabled={isCreating || isLaunching}
          jobs={availableJobs}
          selectedJobs={selectedJobs}
          onJobsChange={handleJobsChange}
          personaNames={personaNames}
          persona={persona}
          onPersonaChange={handlePersonaChange}
          onOpenPersonaInfo={() => setPersonaInfoOpen(true)}
          microphones={voiceDictation.devices}
          microphoneId={voiceDictation.selectedDeviceId}
          onMicrophoneChange={voiceDictation.selectDevice}
          isManager={isManager}
          chiefOfStaff={mode === 'chief-of-staff'}
          onChiefOfStaffChange={alertMode ? undefined : (on) => setMode(on ? 'chief-of-staff' : 'normal')}
        />

        <PersonaInfoModal
          open={personaInfoOpen}
          onClose={() => setPersonaInfoOpen(false)}
          personaName={persona}
          customPersonas={customPersonasForModals}
        />

        {/* Info, not error: nothing went wrong, a choice is missing. */}
        <Snackbar
          open={noJobsNotice}
          autoHideDuration={6000}
          onClose={() => setNoJobsNotice(false)}
          {...snackbarPlacement('top', snackbarTopOffset)}
        >
          <Alert
            severity="info"
            variant="filled"
            onClose={() => setNoJobsNotice(false)}
            data-testid="agent-start-no-jobs-notice"
            action={
              <Button color="inherit" size="small" onClick={() => { setNoJobsNotice(false); setOptionsOpen(true) }}>
                Pick jobs
              </Button>
            }
          >
            You need at least one job to start a session.
          </Alert>
        </Snackbar>
      </Box>
    </Box>
  )
}
